import { Avatar, Box, CircularProgress, IconButton, Skeleton, Theme, Typography } from '@mui/material'
import { BlueEditIcon, DeleteRedIcon } from '../../assets/SvgIcons';

export default function BankCard(props: any): JSX.Element {
    const { isLoading, bank, index, editDialogOpen, setEditDialogOpen, handleEditBank, handleDeleteBank, otpLoading, totalBanks } = props;
    const { bankName, address1 = "", ifscCode, bankAccountNumber, bankAccountType, micrCode, defaultBankAccount, mandateFlag = false, bseStatus } = bank || {};
    const editDeleteButtonDisabled = `${bseStatus ?? ''}`.toLowerCase() === "pending";


    return (
        <>
            {isLoading && <BankCardSkeleton />}
            {!isLoading && (
                <Box sx={(theme: Theme) => ({
                    p: { xs: "5px", sm: "18px" },
                    bgcolor: "background.paper",
                    boxShadow: `0px 4px 5px 0px ${theme.palette.common.black}0D`,
                    borderRadius: '10px',
                    border: `1px solid ${theme.palette.grey[100]}`,
                    height: '100%'
                })}>
                    <Box>
                        <Box sx={(theme: Theme) => ({
                            paddingBottom: '10px', borderBottom: `1px solid ${theme.palette.grey[100]}`, borderWidth: '2px'
                        })}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant='overline_600' sx={(theme: Theme) => ({
                                    color: theme.palette.primary[900]
                                })}>
                                    {`Bank ${index + 1} Details`}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                                    <IconButton
                                        disabled={editDeleteButtonDisabled}
                                        sx={{
                                            '&.Mui-disabled': {
                                                opacity: 0.4,
                                                pointerEvents: 'auto',
                                                cursor: 'not-allowed',
                                            },
                                        }}
                                        onClick={() => handleEditBank(bank, index)}>
                                        <BlueEditIcon />
                                    </IconButton>

                                    {(!mandateFlag && !defaultBankAccount && totalBanks?.length > 1) && (
                                        <IconButton
                                            disabled={editDeleteButtonDisabled}
                                            sx={{
                                                '&.Mui-disabled': {
                                                    opacity: 0.4,
                                                    pointerEvents: 'auto',
                                                    cursor: 'not-allowed',
                                                },
                                            }}
                                            onClick={() => handleDeleteBank(bank)}>
                                            {(otpLoading?.deleteBank && otpLoading?.bankId === bank?.id) ? <CircularProgress size={15} /> : <DeleteRedIcon />}
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '15px', alignItems: 'center', mt: '20px' }}>
                                <Box sx={{ display: 'flex', gap: '15px', alignItems: 'flex-start', flex: 1.5 }}>
                                    <Avatar sx={{ width: '30px', height: '30px', borderRadius: "0px" }} alt='bank name' src='/images/amcIcons/default.svg' />
                                    <Box>
                                        <Typography color='text.primary' variant='subtitle_500'>
                                            {bankName}
                                        </Typography>
                                        <Typography color='text.secondary' variant='overline_600'>
                                            {address1}
                                        </Typography>
                                    </Box>
                                </Box>
                                {defaultBankAccount && (
                                    <Box sx={(theme: Theme) => ({
                                        flex: 0.5, borderRadius: '10px', background: theme.palette.primary[100], display: 'flex', justifyContent: 'center', alignItems: 'center', width: 'fit-content'
                                    })}>
                                        <Typography color='text.primary' variant='subtitle_500'>
                                            Default
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ mt: '20px' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            {/* Left Column */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography variant="overline_600" color="text.secondary">IFSC Code</Typography>
                                    <Typography variant="overline_600" color="text.primary" sx={{ mt: '10px' }}>{ifscCode || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="overline_600" color="text.secondary">MICR Code</Typography>
                                    <Typography variant="overline_600" color="text.primary" sx={{ mt: '10px' }}>{micrCode || '-'}</Typography>
                                </Box>
                            </Box>

                            {/* Right Column */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'right' }}>
                                <Box>
                                    <Typography variant="overline_600" color="text.secondary">Account Number</Typography>
                                    <Typography variant="overline_600" color="text.primary" sx={{ mt: '10px' }}>{bankAccountNumber || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="overline_600" color="text.secondary">Account Type</Typography>
                                    <Typography variant="overline_600" color="text.primary" sx={{ mt: '10px' }}>{bankAccountType || '-'}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </>
    )
}

export function BankCardSkeleton(): JSX.Element {
    return (
        <Box
            sx={(theme: Theme) => ({
                p: '18px',
                bgcolor: 'background.paper',
                boxShadow: `0px 4px 5px 0px ${theme.palette.common.black}0D`,
                borderRadius: '10px',
                border: `1px solid ${theme.palette.grey[100]}`,
            })}
        >
            <Box>
                {/* Header */}
                <Box
                    sx={(theme: Theme) => ({
                        paddingBottom: '10px',
                        borderBottom: `1px solid ${theme.palette.grey[100]}`,
                        borderWidth: '2px',
                    })}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Skeleton variant="text" width={120} height={20} />
                    </Box>

                    {/* Bank Logo and Address */}
                    <Box sx={{ display: 'flex', gap: '15px', alignItems: 'flex-start', mt: '20px' }}>
                        <Skeleton variant="rectangular" width={45} height={45} sx={{ borderRadius: '8px' }} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width="60%" height={24} />
                            <Skeleton variant="text" width="40%" height={20} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Info Columns */}
            <Box sx={{ mt: '20px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    {/* Left Column */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {[1, 2].map((_, i) => (
                            <Box key={i}>
                                <Skeleton variant="text" width="40%" height={20} />
                                <Skeleton variant="text" width="80%" height={22} sx={{ mt: '10px' }} />
                            </Box>
                        ))}
                    </Box>

                    {/* Right Column */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'right' }}>
                        {[3, 4].map((_, i) => (
                            <Box key={i}>
                                <Skeleton variant="text" width="40%" height={20} sx={{ ml: 'auto' }} />
                                <Skeleton variant="text" width="80%" height={22} sx={{ mt: '10px', ml: 'auto' }} />
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}